/**
 * Auth0 Universal Login, as the client does it (see Roxxy.Conversation.Demo `src/lib/auth/auth0.ts` for the
 * full reasoning behind each option). The only differences: the Roxxy-Employee application, and an optional
 * connection.
 *
 * 🔴 `useRefreshTokens: true` with a memory (or, in non-prod, sessionStorage) cache: the access token lives
 * minutes, and an admin editing an experience for half an hour must not be bounced to login mid-save.
 * It needs "Allow Offline Access" on the `https://api.kickfees.io` API, or Auth0 silently issues no refresh
 * token.
 */

import { createAuth0, type Auth0VueClient, type ICache } from '@auth0/auth0-vue';
import { effectScope, watchEffect, type App } from 'vue';
import type { AppConfig } from '../config';
import { AuthNotReadyError, useAuthStore } from '../../stores/auth';

export const AUTH_CALLBACK_PATH = '/callback';

export function callbackUrl(): string {
  return `${window.location.origin}${AUTH_CALLBACK_PATH}`;
}

/**
 * Survives a reload in non-prod only. sessionStorage dies with the tab; localStorage would be a long-lived
 * credential at rest. Two conditions, and `environment` is the one no config flag can override.
 */
export function authSessionPersists(config: AppConfig): boolean {
  return config.environment !== 'prod' && config.features['persistAuthSession'] === true;
}

function sessionScopedCache(): ICache {
  const prefix = 'roxxy.admin.auth.';
  return {
    get<T>(key: string): T | undefined {
      const raw = sessionStorage.getItem(prefix + key);
      if (raw === null) {
        return undefined;
      }
      try {
        return JSON.parse(raw) as T;
      } catch {
        return undefined;
      }
    },
    set<T>(key: string, entry: T): void {
      try {
        sessionStorage.setItem(prefix + key, JSON.stringify(entry));
      } catch {
        // Private-mode Safari throws; no persistence is the right outcome.
      }
    },
    remove(key: string): void {
      sessionStorage.removeItem(prefix + key);
    },
    allKeys(): string[] {
      return Object.keys(sessionStorage)
        .filter((k) => k.startsWith(prefix))
        .map((k) => k.slice(prefix.length));
    },
  };
}

function authorizationParams(config: AppConfig) {
  return {
    audience: config.auth0.audience,
    redirect_uri: callbackUrl(),
    ...(config.auth0.connection === null ? {} : { connection: config.auth0.connection }),
  };
}

export function createAuth0Plugin(config: AppConfig) {
  return createAuth0(
    {
      domain: config.auth0.domain,
      clientId: config.auth0.clientId,
      authorizationParams: authorizationParams(config),
      ...(authSessionPersists(config)
        ? { cache: sessionScopedCache() }
        : { cacheLocation: 'memory' as const }),
      useRefreshTokens: true,
      useRefreshTokensFallback: false,
    },
    { skipRedirectCallback: false },
  );
}

/** Bridges the SDK's reactive state into the store; reactive, because `isLoading` settles several ticks late. */
export function bindAuth0ToStore(app: App): void {
  const client = app.config.globalProperties.$auth0 as Auth0VueClient | undefined;
  if (client === undefined) {
    throw new Error('createAuth0Plugin must be installed before bindAuth0ToStore');
  }
  const auth = useAuthStore();
  auth.setProvider(async () => {
    const token = await client.getAccessTokenSilently();
    if (token === undefined) {
      throw new AuthNotReadyError();
    }
    return token;
  });

  effectScope(true).run(() => {
    watchEffect(() => {
      auth.setLoading(client.isLoading.value);
      const u = client.user.value;
      auth.setAuthenticated(
        client.isAuthenticated.value,
        u === undefined ? null : { sub: u.sub ?? '', email: u.email ?? null, name: u.name ?? null },
      );
      const err = client.error.value;
      auth.setError(err === undefined || err === null ? null : err.message);
    });
  });
}

export async function login(client: Auth0VueClient, config: AppConfig): Promise<void> {
  await client.loginWithRedirect({ authorizationParams: authorizationParams(config) });
}

export async function logout(client: Auth0VueClient): Promise<void> {
  useAuthStore().setAuthenticated(false, null);
  await client.logout({ logoutParams: { returnTo: window.location.origin } });
}
